<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength } from '@vuelidate/validators';
import { ATTRIBUTE_TYPES, IDS_WITH_OPTIONS } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import OptionListEditor from './OptionListEditor.vue';

export default {
  components: {
    NextButton,
    OptionListEditor,
  },
  props: {
    selectedAttribute: {
      type: Object,
      default: () => {},
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onClose'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeType: 0,
      halfWidth: false,
      attributeKey: '',
      values: [],
      isDirty: false,
      isLoading: false,
    };
  },
  validations: {
    description: { required, minLength: minLength(1) },
  },
  computed: {
    types() {
      return ATTRIBUTE_TYPES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    modelLabel() {
      const model = this.selectedAttribute.attribute_model;
      if (model === 3) return 'Oportunidade';
      if (model === 1) return 'Contato';
      return 'Conversa';
    },
    isButtonDisabled() {
      return !this.isDirty || this.v$.description.$invalid;
    },
    isAttributeTypeWithOptions() {
      return IDS_WITH_OPTIONS.includes(this.attributeType);
    },
    selectedAttributeType() {
      return this.types.find(
        item =>
          item.key.toLowerCase() ===
          this.selectedAttribute.attribute_display_type
      )?.id;
    },
    typeGroupLabel() {
      const textIds = [0, 4, 8, 15];
      const numberIds = [1, 2, 3, 10, 16, 17];
      const listIds = [6, 12];
      const checkIds = [13, 14];
      const fieldsIds = [9, 18];
      if (textIds.includes(this.attributeType)) return 'Texto';
      if (numberIds.includes(this.attributeType)) return 'Número';
      if (listIds.includes(this.attributeType)) return 'Lista';
      if (checkIds.includes(this.attributeType)) return 'Caixa';
      if (fieldsIds.includes(this.attributeType)) return 'Campos';
      if (this.attributeType === 5) return 'Data';
      if (this.attributeType === 11) return 'Arquivo';
      return '';
    },
    typeActiveClass() {
      const blue = '!border-2 !border-n-blue-6 !text-n-blue-11 !bg-n-blue-2';
      const teal = '!border-2 !border-n-teal-6 !text-n-teal-11 !bg-n-teal-2';
      const amber =
        '!border-2 !border-n-amber-6 !text-n-amber-11 !bg-n-amber-2';
      const orange =
        '!border-2 !border-n-orange-6 !text-n-orange-11 !bg-n-orange-2';
      const gray = '!border-2 !border-n-gray-6 !text-n-gray-11 !bg-n-gray-2';
      const violet =
        '!border-2 !border-n-violet-6 !text-n-violet-11 !bg-n-violet-2';
      const ruby = '!border-2 !border-n-ruby-6 !text-n-ruby-11 !bg-n-ruby-2';
      const map = {
        0: blue,
        4: blue,
        8: blue,
        15: blue,
        1: teal,
        2: teal,
        3: teal,
        10: teal,
        16: teal,
        17: teal,
        12: amber,
        6: amber,
        13: orange,
        14: orange,
        9: gray,
        18: gray,
        5: violet,
        11: ruby,
      };
      return map[this.attributeType] ?? blue;
    },
  },
  watch: {
    description() {
      if (!this.isLoading) this.isDirty = true;
    },
  },
  mounted() {
    this.isLoading = true;
    this.setFormValues();
    this.$nextTick(() => {
      this.isLoading = false;
      this.isDirty = false;
    });
  },
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    onValuesChange(newValues) {
      this.values = newValues;
      if (!this.isLoading) this.isDirty = true;
    },
    setFormValues() {
      this.displayName = this.selectedAttribute.attribute_display_name;
      this.description = this.selectedAttribute.attribute_description ?? '';
      this.attributeType = this.selectedAttributeType;
      this.attributeKey = this.selectedAttribute.attribute_key;
      this.halfWidth = this.selectedAttribute.half_width ?? false;
      this.values = this.selectedAttribute.attribute_values ?? [];
      this.isDirty = false;
      this.$nextTick(() => {
        if (this.$refs.descriptionDiv) {
          this.$refs.descriptionDiv.innerText = this.description;
        }
      });
    },
    async editAttributes() {
      this.v$.$touch();
      if (this.v$.description.$invalid) {
        return;
      }
      try {
        const payload = {
          id: this.selectedAttribute.id,
          attribute_description: this.description,
        };
        if (this.isAttributeTypeWithOptions) {
          payload.attribute_values = this.values.filter(v => v.label && v.key);
        }
        await this.$store.dispatch('attributes/update', payload);
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.EDIT.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.EDIT.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="flex flex-col h-auto overflow-auto">
    <form class="flex w-full" @submit.prevent="editAttributes">
      <div class="w-full flex flex-col gap-3 pt-2">
        <!-- Nome inline (readonly) -->
        <div class="text-base font-semibold text-n-slate-12 truncate">
          {{ displayName }}
        </div>

        <!-- Aplicação + Chave lado a lado -->
        <div class="flex items-start gap-3">
          <!-- Aplicação (readonly) -->
          <div class="flex flex-col gap-0.5 flex-1">
            <span
              class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
            </span>
            <div
              class="flex items-center justify-between gap-1 bg-n-alpha-1 rounded px-2 py-1 min-h-[30px] opacity-60 cursor-not-allowed"
            >
              <span class="text-sm text-n-slate-12 truncate flex-1">{{
                modelLabel
              }}</span>
              <span
                class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
              />
            </div>
          </div>

          <!-- Chave (readonly) -->
          <div class="flex flex-col gap-0.5 flex-1">
            <span
              class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL') }}
            </span>
            <div
              class="flex items-center bg-n-alpha-1 rounded px-2 py-1 min-h-[30px] opacity-60 cursor-not-allowed"
            >
              <span class="text-sm text-n-slate-12 truncate flex-1">{{
                attributeKey
              }}</span>
            </div>
          </div>
        </div>

        <!-- Descrição (editável, auto-crescente) -->
        <div class="flex flex-col gap-0.5">
          <span
            class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
          </span>
          <div
            ref="descriptionDiv"
            contenteditable="true"
            :data-placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
            class="text-sm px-2 py-1.5 rounded bg-n-alpha-1 hover:bg-n-alpha-2 focus:bg-n-alpha-2 text-n-slate-12 min-h-[30px] w-full outline-none overflow-hidden transition-colors [word-break:break-word] [overflow-wrap:break-word] [white-space:pre-wrap] empty:before:content-[attr(data-placeholder)] empty:before:text-n-slate-9"
            @input="
              e => {
                description = e.target.innerText;
              }
            "
            @blur="
              e => {
                description = e.target.innerText;
              }
            "
          />
        </div>

        <!-- Tipo (readonly) -->
        <div class="flex flex-col gap-0.5">
          <span
            class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
          </span>
          <div>
            <span
              class="px-3 py-1.5 text-sm font-medium rounded-lg"
              :class="typeActiveClass"
            >
              {{ typeGroupLabel }}
            </span>
          </div>
        </div>

        <!-- Opções de lista (editável, chave travada) -->
        <div v-if="isAttributeTypeWithOptions" class="flex flex-col gap-0.5">
          <span
            class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.LABEL') }}
          </span>
          <div class="rounded-xl border border-n-weak py-2">
            <OptionListEditor
              :model-value="values"
              :show-placeholder="attributeType === 9 || attributeType === 18"
              lock-keys
              @update:model-value="onValuesChange"
            />
          </div>
        </div>

        <!-- Proporção 50% + Salvar -->
        <div class="flex items-end gap-4">
          <div
            class="flex items-center gap-2 shrink-0 opacity-50 cursor-not-allowed"
          >
            <span class="text-sm font-medium text-n-slate-11">
              Proporção 50%
            </span>
            <div
              class="relative w-9 h-5 rounded-full"
              :class="halfWidth ? 'bg-n-brand' : 'bg-n-slate-5'"
            >
              <div
                class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                :class="halfWidth ? 'translate-x-4' : 'translate-x-0'"
              />
            </div>
          </div>
          <NextButton
            type="submit"
            label="Salvar"
            class="ml-auto !h-[30px] !text-xs !px-3"
            :is-loading="isUpdating"
            :disabled="isButtonDisabled"
          />
        </div>
      </div>
    </form>
  </div>
</template>
